//T.32832 - Servicio Helper para Google Maps - Optimizado para minimizar consumo de API
import { Injectable } from '@angular/core';
import { GoogleMapsConfigService } from './google-maps-config.service';

/**
 * Servicio helper para Google Maps
 * Maneja la inicialización y actualización optimizada del mapa
 * Diseñado para cargar el mapa UNA SOLA VEZ y solo actualizar marcadores
 */
@Injectable({
  providedIn: 'root'
})
export class GoogleMapsHelperService {

  // Flag para controlar que el script se cargue una sola vez
  private scriptLoaded = false;
  private scriptLoading = false;
  private scriptCallbacks: Array<() => void> = [];

  constructor(private configService: GoogleMapsConfigService) {
    console.log('T.32832 - GoogleMapsHelperService inicializado');
  }

  /**
   * Carga el script de Google Maps API de forma optimizada
   * Se carga UNA SOLA VEZ para toda la aplicación
   * @returns Promise que se resuelve cuando el script está listo
   */
  public loadGoogleMapsScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Si ya está cargado, resolver inmediatamente
      if (this.scriptLoaded) {
        console.log('T.32832 - Google Maps script ya está cargado');
        resolve();
        return;
      }

      // Si está en proceso de carga, agregar callback
      if (this.scriptLoading) {
        console.log('T.32832 - Google Maps script cargando, agregando callback');
        this.scriptCallbacks.push(() => resolve());
        return;
      }

      // Verificar que la API Key esté configurada
      if (!this.configService.isApiKeyConfigured()) {
        const error = 'Google Maps API Key no configurada. Por favor configure la key en google-maps-config.service.ts';
        console.error('T.32832 -', error);
        reject(new Error(error));
        return;
      }

      // Iniciar carga del script
      this.scriptLoading = true;
      console.log('T.32832 - Cargando Google Maps script...');

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.configService.getApiKey()}`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        console.log('T.32832 - Google Maps script cargado exitosamente');
        this.scriptLoaded = true;
        this.scriptLoading = false;
        
        // Resolver todos los callbacks pendientes
        this.scriptCallbacks.forEach(callback => callback());
        this.scriptCallbacks = [];
        
        resolve();
      };

      script.onerror = (error) => {
        console.error('T.32832 - Error al cargar Google Maps script:', error);
        this.scriptLoading = false;
        reject(new Error('Error al cargar Google Maps API'));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Inicializa un mapa de Google Maps
   * @param elementId ID del elemento HTML donde se renderizará el mapa
   * @param center Centro inicial del mapa
   * @returns Instancia del mapa de Google Maps
   */
  public async initializeMap(
    elementId: string, 
    center: google.maps.LatLngLiteral
  ): Promise<google.maps.Map> {
    // Asegurar que el script esté cargado
    await this.loadGoogleMapsScript();

    const mapElement = document.getElementById(elementId);
    if (!mapElement) {
      throw new Error(`Elemento con ID '${elementId}' no encontrado`);
    }

    const mapOptions = this.configService.getMapOptions(center);
    const map = new google.maps.Map(mapElement, mapOptions);

    console.log('T.32832 - Mapa de Google Maps inicializado');
    return map;
  }

  /**
   * Crea o actualiza un marcador en el mapa
   * OPTIMIZACIÓN: No recarga el mapa, solo actualiza la posición del marcador
   * @param map Instancia del mapa
   * @param markerId ID único del marcador
   * @param position Posición del marcador
   * @param options Opciones adicionales del marcador
   * @param existingMarkers Mapa de marcadores existentes
   * @returns El marcador creado o actualizado
   */
  public createOrUpdateMarker(
    map: google.maps.Map,
    markerId: string,
    position: google.maps.LatLngLiteral,
    options: {
      title?: string;
      icon?: string | google.maps.Icon | google.maps.Symbol;
      label?: string | google.maps.MarkerLabel;
      customHtml?: string;
    },
    existingMarkers: Map<string, google.maps.Marker>
  ): google.maps.Marker {
    
    // Si el marcador ya existe, solo actualizar su posición
    if (existingMarkers.has(markerId)) {
      const marker = existingMarkers.get(markerId)!;
      marker.setPosition(position);
      
      // Actualizar título si cambió
      if (options.title && marker.getTitle() !== options.title) {
        marker.setTitle(options.title);
      }
      
      console.log('T.32832 - Marcador actualizado:', markerId);
      return marker;
    }

    // Crear nuevo marcador
    const markerOptions: google.maps.MarkerOptions = {
      position: position,
      map: map,
      title: options.title,
      optimized: this.configService.getMarkerConfig().OPTIMIZED
    };

    // Agregar icono personalizado si se proporciona
    if (options.icon) {
      markerOptions.icon = options.icon;
    }

    // Agregar etiqueta si se proporciona
    if (options.label) {
      markerOptions.label = options.label;
    }

    const marker = new google.maps.Marker(markerOptions);
    existingMarkers.set(markerId, marker);
    
    console.log('T.32832 - Nuevo marcador creado:', markerId);
    return marker;
  }

  /**
   * Crea un InfoWindow para un marcador
   * @param marker Marcador al que se asociará el InfoWindow
   * @param content Contenido HTML del InfoWindow
   * @returns Instancia del InfoWindow
   */
  public createInfoWindow(
    marker: google.maps.Marker,
    content: string
  ): google.maps.InfoWindow {
    const infoWindow = new google.maps.InfoWindow({
      content: content
    });

    marker.addListener('click', () => {
      infoWindow.open(marker.getMap() as google.maps.Map, marker);
    });

    return infoWindow;
  }

  /**
   * Crea un círculo en el mapa (útil para geofence)
   * @param map Instancia del mapa
   * @param center Centro del círculo
   * @param radius Radio en metros
   * @param options Opciones de estilo del círculo
   * @returns Instancia del círculo
   */
  public createCircle(
    map: google.maps.Map,
    center: google.maps.LatLngLiteral,
    radius: number,
    options?: {
      strokeColor?: string;
      strokeOpacity?: number;
      strokeWeight?: number;
      fillColor?: string;
      fillOpacity?: number;
    }
  ): google.maps.Circle {
    const circleOptions: google.maps.CircleOptions = {
      map: map,
      center: center,
      radius: radius,
      strokeColor: options?.strokeColor || '#FF0000',
      strokeOpacity: options?.strokeOpacity || 0.8,
      strokeWeight: options?.strokeWeight || 2,
      fillColor: options?.fillColor || '#FF0000',
      fillOpacity: options?.fillOpacity || 0.1
    };

    return new google.maps.Circle(circleOptions);
  }

  /**
   * Crea una polilínea en el mapa (útil para rutas)
   * @param map Instancia del mapa
   * @param path Array de coordenadas
   * @param options Opciones de estilo
   * @returns Instancia de la polilínea
   */
  public createPolyline(
    map: google.maps.Map,
    path: google.maps.LatLngLiteral[],
    options?: {
      strokeColor?: string;
      strokeOpacity?: number;
      strokeWeight?: number;
    }
  ): google.maps.Polyline {
    const polylineOptions: google.maps.PolylineOptions = {
      map: map,
      path: path,
      strokeColor: options?.strokeColor || '#0000FF',
      strokeOpacity: options?.strokeOpacity || 0.7,
      strokeWeight: options?.strokeWeight || 3
    };

    return new google.maps.Polyline(polylineOptions);
  }

  /**
   * Centra el mapa en una posición específica
   * @param map Instancia del mapa
   * @param position Posición donde centrar
   * @param zoom Nivel de zoom (opcional)
   */
  public centerMap(
    map: google.maps.Map,
    position: google.maps.LatLngLiteral,
    zoom?: number
  ): void {
    map.setCenter(position);
    if (zoom !== undefined) {
      map.setZoom(zoom);
    }
  }

  /**
   * Ajusta el mapa para mostrar todos los marcadores
   * @param map Instancia del mapa
   * @param positions Array de posiciones a incluir
   */
  public fitBounds(
    map: google.maps.Map,
    positions: google.maps.LatLngLiteral[]
  ): void {
    if (positions.length === 0) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    positions.forEach(pos => bounds.extend(pos));
    map.fitBounds(bounds);
  }

  /**
   * Elimina un marcador del mapa
   * @param markerId ID del marcador a eliminar
   * @param existingMarkers Mapa de marcadores existentes
   */
  public removeMarker(
    markerId: string,
    existingMarkers: Map<string, google.maps.Marker>
  ): void {
    if (existingMarkers.has(markerId)) {
      const marker = existingMarkers.get(markerId)!;
      marker.setMap(null);
      existingMarkers.delete(markerId);
      console.log('T.32832 - Marcador eliminado:', markerId);
    }
  }

  /**
   * Limpia todos los marcadores del mapa
   * @param existingMarkers Mapa de marcadores existentes
   */
  public clearAllMarkers(existingMarkers: Map<string, google.maps.Marker>): void {
    existingMarkers.forEach(marker => marker.setMap(null));
    existingMarkers.clear();
    console.log('T.32832 - Todos los marcadores eliminados');
  }

  /**
   * Calcula la distancia entre dos puntos usando fórmula de Haversine
   * @param lat1 Latitud punto 1
   * @param lon1 Longitud punto 1
   * @param lat2 Latitud punto 2
   * @param lon2 Longitud punto 2
   * @returns Distancia en metros
   */
  public calculateDistance(
    lat1: number, 
    lon1: number, 
    lat2: number, 
    lon2: number
  ): number {
    const R = 6371000; // Radio de la Tierra en metros
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Convierte grados a radianes
   */
  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Formatea distancia para mostrar
   */
  public formatDistance(distanceMeters: number): string {
    if (distanceMeters < 1000) {
      return `${Math.round(distanceMeters)}m`;
    } else {
      return `${(distanceMeters / 1000).toFixed(1)}km`;
    }
  }
}
